import React, { useState, useEffect, useCallback } from 'react';
import { Info, RefreshCw } from 'lucide-react';
import apiService from '../../../apiService';
import { fmtMoney, Card, Spinner } from '../financeShared';

export default function BalanceSheetTab({ currency, rate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const fmt = (n) => fmtMoney(n, currency, rate);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.get('/admin/finance/balance-sheet', { userType: 'admin' });
      if (res?.success) setData(res);
    } catch (e) { console.error('balance-sheet:', e); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <Spinner />;
  if (!data) return <p style={{ color: '#9AA0A8' }}>Aucune donnée.</p>;

  const { assets, liabilities, equity, breakdown } = data;
  const totalAssets = (assets.treasuryEstimate || 0) + (assets.receivablesFromUsers || 0);

  const Line = ({ label, value, bold, color }) => (
    <tr style={{ borderBottom: '1px solid #F4F5F7' }}>
      <td style={{ padding: '0.55rem 0', color: bold ? '#1E2229' : '#414852', fontWeight: bold ? 700 : 400 }}>{label}</td>
      <td style={{ padding: '0.55rem 0', textAlign: 'right', fontWeight: bold ? 700 : 600, color: color || '#1E2229' }}>{fmt(value)}</td>
    </tr>
  );

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
        <button className="btn-secondary" onClick={load} style={{ padding: '0.4rem 1rem', display: 'flex', alignItems: 'center', gap: 6 }}><RefreshCw size={15} /> Actualiser</button>
      </div>

      <div style={{ background: '#FEE9E2', border: '1px solid #bfdbfe', borderRadius: '10px', padding: '0.85rem 1rem', marginBottom: '1.5rem', color: '#C9350F', fontSize: '0.83rem', display: 'flex', gap: 10 }}>
        <Info size={18} style={{ flexShrink: 0, marginTop: 2 }} />
        <span>{data.disclaimer}</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
        <Card>
          <h3 style={{ marginBottom: '1rem', color: '#10b981', fontSize: '1rem', fontWeight: 700 }}>Actif</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
            <tbody>
              <Line label="Trésorerie estimée (flux MM)" value={assets.treasuryEstimate} />
              <Line label="Créances sur utilisateurs" value={assets.receivablesFromUsers} />
              <Line label="Total actif" value={totalAssets} bold color="#10b981" />
            </tbody>
          </table>
          <p style={{ fontSize: '0.72rem', color: '#9AA0A8', marginTop: '0.6rem' }}>Solde des comptes plateforme (ledger) : {fmt(assets.platformWalletBalance)}</p>
        </Card>

        <Card>
          <h3 style={{ marginBottom: '1rem', color: '#dc2626', fontSize: '1rem', fontWeight: 700 }}>Passif</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
            <tbody>
              <Line label="Dettes envers utilisateurs (soldes)" value={liabilities.payablesToUsers} />
              <Line label="Total passif" value={liabilities.payablesToUsers} bold color="#dc2626" />
            </tbody>
          </table>
        </Card>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
        <Card>
          <h3 style={{ marginBottom: '1rem', color: '#1E2229', fontSize: '1rem', fontWeight: 700 }}>Résultat cumulé</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
            <tbody>
              <Line label="Revenus cumulés (depuis l'origine)" value={equity.cumulativeRevenue} color="#10b981" />
              <Line label="Charges cumulées (OPEX)" value={equity.cumulativeExpenses} color="#dc2626" />
              <Line label="Résultat cumulé" value={equity.cumulativeResult} bold color={equity.cumulativeResult >= 0 ? '#10b981' : '#dc2626'} />
            </tbody>
          </table>
        </Card>

        <Card>
          <h3 style={{ marginBottom: '1rem', color: '#1E2229', fontSize: '1rem', fontWeight: 700 }}>Détail soldes utilisateurs</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead><tr style={{ color: '#9AA0A8', borderBottom: '1px solid #E9EBEF' }}>
              <th style={{ textAlign: 'left', padding: '0.4rem 0' }}>Type</th>
              <th style={{ textAlign: 'right', padding: '0.4rem 0' }}>Dettes</th>
              <th style={{ textAlign: 'right', padding: '0.4rem 0' }}>Créances</th>
            </tr></thead>
            <tbody>
              {[['Clients', breakdown.clients], ['Chauffeurs', breakdown.drivers], ['Fournisseurs', breakdown.suppliers]].map(([label, b]) => (
                <tr key={label} style={{ borderBottom: '1px solid #F4F5F7' }}>
                  <td style={{ padding: '0.5rem 0', color: '#414852' }}>{label}</td>
                  <td style={{ padding: '0.5rem 0', textAlign: 'right', color: '#dc2626', fontWeight: 600 }}>{fmt(b.payable)}</td>
                  <td style={{ padding: '0.5rem 0', textAlign: 'right', color: '#10b981', fontWeight: 600 }}>{fmt(b.receivable)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
    </>
  );
}
